import { createSlice, createAsyncThunk, SerializedError } from '@reduxjs/toolkit'
import { LoadingState, Redux } from '../..'
import { AxiosError } from 'axios'
import {
  MenuEquipment,
  CreateMenuEquipmentDto,
  UpdateMenuEquipmentDto,
  MenuEquipmentProductMapping,
  CreateMenuEquipmentProductMappingDto,
  UpdateMenuEquipmentProductMappingDto,
  BulkUpsertMenuEquipmentProductMappingDto,
  BulkUpsertMenuEquipmentProductMappingResponse,
  RestaurantMenuEquipment,
  CreateRestaurantMenuEquipmentDto,
  UpdateRestaurantMenuEquipmentDto,
  BulkUpsertRestaurantMenuEquipmentResponse,
  BulkUpdateStatusRequestDto,
  BulkUpdateStatusResponse,
  PreviewResponse
} from '@/src/modules/menu-equipments/types/menuEquipmentTypes'
import {
  FETCH_MENU_EQUIPMENTS,
  FETCH_MENU_EQUIPMENT_BY_ID,
  CREATE_MENU_EQUIPMENT,
  UPDATE_MENU_EQUIPMENT,
  DELETE_MENU_EQUIPMENT,
  FETCH_EQUIPMENT_MAPPINGS,
  FETCH_EQUIPMENT_MAPPING_BY_ID,
  CREATE_EQUIPMENT_MAPPING,
  UPDATE_EQUIPMENT_MAPPING,
  DELETE_EQUIPMENT_MAPPING,
  BULK_UPSERT_EQUIPMENT_MAPPINGS,
  BULK_UPSERT_RESTAURANT_MENU_EQUIPMENTS,
  FETCH_RESTAURANT_MENU_EQUIPMENTS,
  UPDATE_RESTAURANT_MENU_EQUIPMENT,
  CREATE_RESTAURANT_MENU_EQUIPMENT,
  BULK_UPDATE_RESTAURANT_MENU_EQUIPMENT_STATUS,
  PREVIEW_BULK_UPDATE_RESTAURANT_MENU_EQUIPMENT_STATUS
} from './actions'

const MENU_EQUIPMENT_BASE_URL = '/resto365-menu-equipments'
const MENU_EQUIPMENT_PRODUCTS_BASE_URL = '/resto365-menu-equipment-products'
const RESTAURANT_MENU_EQUIPMENT_BASE_URL = '/resto365-restaurant-menu-equipments'

interface RootState {
  menuEquipments: StateSlice<MenuEquipment[]>
  menuEquipment: StateSlice<MenuEquipment | null>
  createdMenuEquipment: StateSlice<MenuEquipment | null>
  updatedMenuEquipment: StateSlice<MenuEquipment | null>
  equipmentMappings: StateSlice<MenuEquipmentProductMapping[]>
  equipmentMapping: StateSlice<MenuEquipmentProductMapping | null>
  createdEquipmentMapping: StateSlice<MenuEquipmentProductMapping | null>
  updatedEquipmentMapping: StateSlice<MenuEquipmentProductMapping | null>
  bulkUpsertEquipmentMappings: StateSlice<MenuEquipmentProductMapping[]>
  restaurantMenuEquipments: StateSlice<RestaurantMenuEquipment[]>
  bulkUpsertRestaurantMenuEquipments: StateSlice<RestaurantMenuEquipment[]>
}

type StateSlice<T> = {
  value: T
  loadingState: LoadingState
  error: SerializedError | null
}

type InitialStateType<K> = K extends StateSlice<infer T> ? T : never

const getInitialStateFor = <K>(initialState: InitialStateType<K>) => {
  return {
    value: initialState,
    loadingState: 'initial' as LoadingState,
    error: null
  }
}

const initialState: RootState = {
  menuEquipments: getInitialStateFor<RootState['menuEquipments']>([]),
  menuEquipment: getInitialStateFor<RootState['menuEquipment']>(null),
  createdMenuEquipment: getInitialStateFor<RootState['createdMenuEquipment']>(null),
  updatedMenuEquipment: getInitialStateFor<RootState['updatedMenuEquipment']>(null),
  equipmentMappings: getInitialStateFor<RootState['equipmentMappings']>([]),
  equipmentMapping: getInitialStateFor<RootState['equipmentMapping']>(null),
  createdEquipmentMapping: getInitialStateFor<RootState['createdEquipmentMapping']>(null),
  updatedEquipmentMapping: getInitialStateFor<RootState['updatedEquipmentMapping']>(null),
  bulkUpsertEquipmentMappings: getInitialStateFor<RootState['bulkUpsertEquipmentMappings']>([]),
  restaurantMenuEquipments: getInitialStateFor<RootState['restaurantMenuEquipments']>([]),
  bulkUpsertRestaurantMenuEquipments: getInitialStateFor<RootState['bulkUpsertRestaurantMenuEquipments']>([])
}

// Fetch all menu equipments
export const fetchMenuEquipments = createAsyncThunk<MenuEquipment[], void, Redux>(
  FETCH_MENU_EQUIPMENTS,
  async (_, { extra }) => {
    try {
      const response = await extra.httpClient.get<MenuEquipment[] | { data: MenuEquipment[] }>(MENU_EQUIPMENT_BASE_URL)

      // Handle different response structures
      let equipments: MenuEquipment[]
      if (Array.isArray(response.data)) {
        equipments = response.data
      } else if (response.data.data && Array.isArray(response.data.data)) {
        equipments = response.data.data
      } else {
        equipments = []
      }

      // Convert boolean fields appropriately
      return equipments.map(equipment => ({
        ...equipment,
        isActive: Boolean(equipment.isActive)
      }))
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to fetch menu equipments')
      }
      throw new Error('Failed to fetch menu equipments')
    }
  }
)

// Fetch single menu equipment by ID
export const fetchMenuEquipmentById = createAsyncThunk<MenuEquipment, number, Redux>(
  FETCH_MENU_EQUIPMENT_BY_ID,
  async (id: number, { extra }) => {
    try {
      const response = await extra.httpClient.get<MenuEquipment | { data: MenuEquipment }>(
        `${MENU_EQUIPMENT_BASE_URL}/${id}`
      )

      // Handle different response structures
      let equipment: MenuEquipment
      if ('data' in response.data) {
        equipment = response.data.data
      } else {
        equipment = response.data as MenuEquipment
      }

      return {
        ...equipment,
        isActive: Boolean(equipment.isActive)
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to fetch menu equipment')
      }
      throw new Error('Failed to fetch menu equipment')
    }
  }
)

// Create menu equipment
export const createMenuEquipment = createAsyncThunk<MenuEquipment, CreateMenuEquipmentDto, Redux>(
  CREATE_MENU_EQUIPMENT,
  async (data: CreateMenuEquipmentDto, { extra }) => {
    try {
      const response = await extra.httpClient.post<{ data: MenuEquipment }>(MENU_EQUIPMENT_BASE_URL, data)

      return {
        ...response.data.data,
        isActive: Boolean(response.data.data.isActive)
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to create menu equipment')
      }
      throw new Error('Failed to create menu equipment')
    }
  }
)

// Update menu equipment
export const updateMenuEquipment = createAsyncThunk<MenuEquipment, { id: number; data: UpdateMenuEquipmentDto }, Redux>(
  UPDATE_MENU_EQUIPMENT,
  async ({ id, data }, { extra }) => {
    try {
      const response = await extra.httpClient.patch<{ data: MenuEquipment }>(`${MENU_EQUIPMENT_BASE_URL}/${id}`, data)

      return {
        ...response.data.data,
        isActive: Boolean(response.data.data.isActive)
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to update menu equipment')
      }
      throw new Error('Failed to update menu equipment')
    }
  }
)

// Delete menu equipment
export const deleteMenuEquipment = createAsyncThunk<number, number, Redux>(
  DELETE_MENU_EQUIPMENT,
  async (id: number, { extra }) => {
    try {
      await extra.httpClient.delete(`${MENU_EQUIPMENT_BASE_URL}/${id}`)

      return id
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to delete menu equipment')
      }
      throw new Error('Failed to delete menu equipment')
    }
  }
)

const menuEquipmentsSlice = createSlice({
  name: 'menuEquipments',
  initialState,
  reducers: {
    clearCreatedMenuEquipment: state => {
      state.createdMenuEquipment = initialState.createdMenuEquipment
    },
    clearUpdatedMenuEquipment: state => {
      state.updatedMenuEquipment = initialState.updatedMenuEquipment
    },
    clearMenuEquipment: state => {
      state.menuEquipment = initialState.menuEquipment
    },
    invalidateMenuEquipments: state => {
      state.menuEquipments = {
        ...initialState.menuEquipments,
        loadingState: 'invalidated'
      }
    },
    clearMenuEquipments: state => {
      state.menuEquipments = initialState.menuEquipments
    },
    clearEquipmentMappings: state => {
      state.equipmentMappings = initialState.equipmentMappings
    },
    clearEquipmentMapping: state => {
      state.equipmentMapping = initialState.equipmentMapping
    },
    clearCreatedEquipmentMapping: state => {
      state.createdEquipmentMapping = initialState.createdEquipmentMapping
    },
    clearUpdatedEquipmentMapping: state => {
      state.updatedEquipmentMapping = initialState.updatedEquipmentMapping
    },
    clearBulkUpsertEquipmentMappings: state => {
      state.bulkUpsertEquipmentMappings = initialState.bulkUpsertEquipmentMappings
    },
    clearRestaurantMenuEquipments: state => {
      state.restaurantMenuEquipments = initialState.restaurantMenuEquipments
    },
    clearBulkUpsertRestaurantMenuEquipments: state => {
      state.bulkUpsertRestaurantMenuEquipments = initialState.bulkUpsertRestaurantMenuEquipments
    }
  },
  extraReducers: builder => {
    builder

      // Fetch all menu equipments
      .addCase(fetchMenuEquipments.pending, state => {
        state.menuEquipments.loadingState = 'pending'
      })
      .addCase(fetchMenuEquipments.fulfilled, (state, action) => {
        state.menuEquipments.loadingState = 'success'
        state.menuEquipments.value = action.payload
      })
      .addCase(fetchMenuEquipments.rejected, (state, action) => {
        state.menuEquipments.loadingState = 'error'
        state.menuEquipments.error = action.error
      })

      // Fetch single menu equipment
      .addCase(fetchMenuEquipmentById.pending, state => {
        state.menuEquipment.loadingState = 'pending'
      })
      .addCase(fetchMenuEquipmentById.fulfilled, (state, action) => {
        state.menuEquipment.loadingState = 'success'
        state.menuEquipment.value = action.payload
      })
      .addCase(fetchMenuEquipmentById.rejected, (state, action) => {
        state.menuEquipment.loadingState = 'error'
        state.menuEquipment.error = action.error
      })

      // Create menu equipment
      .addCase(createMenuEquipment.pending, state => {
        state.createdMenuEquipment.loadingState = 'pending'
      })
      .addCase(createMenuEquipment.fulfilled, (state, action) => {
        state.createdMenuEquipment.loadingState = 'success'
        state.createdMenuEquipment.value = action.payload

        // Add to list
        state.menuEquipments.value = [...state.menuEquipments.value, action.payload]
      })
      .addCase(createMenuEquipment.rejected, (state, action) => {
        state.createdMenuEquipment.loadingState = 'error'
        state.createdMenuEquipment.error = action.error
      })

      // Update menu equipment
      .addCase(updateMenuEquipment.pending, state => {
        state.updatedMenuEquipment.loadingState = 'pending'
      })
      .addCase(updateMenuEquipment.fulfilled, (state, action) => {
        state.updatedMenuEquipment.loadingState = 'success'
        state.updatedMenuEquipment.value = action.payload

        // Update in list
        const index = state.menuEquipments.value.findIndex(eq => eq.id === action.payload.id)
        if (index !== -1) {
          state.menuEquipments.value[index] = action.payload
        }

        // Update single equipment if it's the same one
        if (state.menuEquipment.value?.id === action.payload.id) {
          state.menuEquipment.value = action.payload
        }
      })
      .addCase(updateMenuEquipment.rejected, (state, action) => {
        state.updatedMenuEquipment.loadingState = 'error'
        state.updatedMenuEquipment.error = action.error
      })

      // Delete menu equipment
      .addCase(deleteMenuEquipment.pending, state => {
        state.menuEquipments.loadingState = 'pending'
      })
      .addCase(deleteMenuEquipment.fulfilled, (state, action) => {
        state.menuEquipments.loadingState = 'success'

        // Remove from list
        state.menuEquipments.value = state.menuEquipments.value.filter(eq => eq.id !== action.payload)

        // Clear single equipment if it was deleted
        if (state.menuEquipment.value?.id === action.payload) {
          state.menuEquipment.value = null
        }
      })
      .addCase(deleteMenuEquipment.rejected, (state, action) => {
        state.menuEquipments.loadingState = 'error'
        state.menuEquipments.error = action.error
      })

      // Fetch equipment mappings
      .addCase(fetchEquipmentMappings.pending, state => {
        state.equipmentMappings.loadingState = 'pending'
      })
      .addCase(fetchEquipmentMappings.fulfilled, (state, action) => {
        state.equipmentMappings.loadingState = 'success'
        state.equipmentMappings.value = action.payload
      })
      .addCase(fetchEquipmentMappings.rejected, (state, action) => {
        state.equipmentMappings.loadingState = 'error'
        state.equipmentMappings.error = action.error
      })

      // Fetch single equipment mapping
      .addCase(fetchEquipmentMappingById.pending, state => {
        state.equipmentMapping.loadingState = 'pending'
      })
      .addCase(fetchEquipmentMappingById.fulfilled, (state, action) => {
        state.equipmentMapping.loadingState = 'success'
        state.equipmentMapping.value = action.payload
      })
      .addCase(fetchEquipmentMappingById.rejected, (state, action) => {
        state.equipmentMapping.loadingState = 'error'
        state.equipmentMapping.error = action.error
      })

      // Create equipment mapping
      .addCase(createEquipmentMapping.pending, state => {
        state.createdEquipmentMapping.loadingState = 'pending'
      })
      .addCase(createEquipmentMapping.fulfilled, (state, action) => {
        state.createdEquipmentMapping.loadingState = 'success'
        state.createdEquipmentMapping.value = action.payload

        // Add to mappings list
        state.equipmentMappings.value = [...state.equipmentMappings.value, action.payload]
      })
      .addCase(createEquipmentMapping.rejected, (state, action) => {
        state.createdEquipmentMapping.loadingState = 'error'
        state.createdEquipmentMapping.error = action.error
      })

      // Update equipment mapping
      .addCase(updateEquipmentMapping.pending, state => {
        state.updatedEquipmentMapping.loadingState = 'pending'
      })
      .addCase(updateEquipmentMapping.fulfilled, (state, action) => {
        state.updatedEquipmentMapping.loadingState = 'success'
        state.updatedEquipmentMapping.value = action.payload

        // Update in mappings list
        const index = state.equipmentMappings.value.findIndex(m => m.id === action.payload.id)
        if (index !== -1) {
          state.equipmentMappings.value[index] = action.payload
        }

        // Update single mapping if it's the same one
        if (state.equipmentMapping.value?.id === action.payload.id) {
          state.equipmentMapping.value = action.payload
        }
      })
      .addCase(updateEquipmentMapping.rejected, (state, action) => {
        state.updatedEquipmentMapping.loadingState = 'error'
        state.updatedEquipmentMapping.error = action.error
      })

      // Delete equipment mapping
      .addCase(deleteEquipmentMapping.pending, state => {
        state.equipmentMappings.loadingState = 'pending'
      })
      .addCase(deleteEquipmentMapping.fulfilled, (state, action) => {
        state.equipmentMappings.loadingState = 'success'

        // Remove from mappings list
        state.equipmentMappings.value = state.equipmentMappings.value.filter(m => m.id !== action.payload)

        // Clear single mapping if it was deleted
        if (state.equipmentMapping.value?.id === action.payload) {
          state.equipmentMapping.value = null
        }
      })
      .addCase(deleteEquipmentMapping.rejected, (state, action) => {
        state.equipmentMappings.loadingState = 'error'
        state.equipmentMappings.error = action.error
      })

      // Bulk upsert equipment mappings
      .addCase(bulkUpsertEquipmentMappings.pending, state => {
        state.bulkUpsertEquipmentMappings.loadingState = 'pending'
      })
      .addCase(bulkUpsertEquipmentMappings.fulfilled, (state, action) => {
        state.bulkUpsertEquipmentMappings.loadingState = 'success'
        state.bulkUpsertEquipmentMappings.value = action.payload

        // Update equipment mappings list with the new mappings
        state.equipmentMappings.value = action.payload
        state.equipmentMappings.loadingState = 'success'
      })
      .addCase(bulkUpsertEquipmentMappings.rejected, (state, action) => {
        state.bulkUpsertEquipmentMappings.loadingState = 'error'
        state.bulkUpsertEquipmentMappings.error = action.error
      })

      // Bulk upsert restaurant menu equipments
      .addCase(bulkUpsertRestaurantMenuEquipments.pending, state => {
        state.bulkUpsertRestaurantMenuEquipments.loadingState = 'pending'
      })
      .addCase(bulkUpsertRestaurantMenuEquipments.fulfilled, (state, action) => {
        state.bulkUpsertRestaurantMenuEquipments.loadingState = 'success'
        state.bulkUpsertRestaurantMenuEquipments.value = action.payload
      })
      .addCase(bulkUpsertRestaurantMenuEquipments.rejected, (state, action) => {
        state.bulkUpsertRestaurantMenuEquipments.loadingState = 'error'
        state.bulkUpsertRestaurantMenuEquipments.error = action.error
      })

      // Fetch restaurant menu equipments
      .addCase(fetchRestaurantMenuEquipments.pending, state => {
        state.restaurantMenuEquipments.loadingState = 'pending'
      })
      .addCase(fetchRestaurantMenuEquipments.fulfilled, (state, action) => {
        state.restaurantMenuEquipments.loadingState = 'success'
        state.restaurantMenuEquipments.value = action.payload
      })
      .addCase(fetchRestaurantMenuEquipments.rejected, (state, action) => {
        state.restaurantMenuEquipments.loadingState = 'error'
        state.restaurantMenuEquipments.error = action.error
      })

      // Create restaurant menu equipment
      .addCase(createRestaurantMenuEquipment.pending, state => {
        state.restaurantMenuEquipments.loadingState = 'pending'
      })
      .addCase(createRestaurantMenuEquipment.fulfilled, (state, action) => {
        state.restaurantMenuEquipments.loadingState = 'success'

        // Add to the list if not already present
        const existingIndex = state.restaurantMenuEquipments.value.findIndex(item => item.id === action.payload.id)
        if (existingIndex === -1) {
          state.restaurantMenuEquipments.value = [...state.restaurantMenuEquipments.value, action.payload]
        } else {
          state.restaurantMenuEquipments.value[existingIndex] = action.payload
        }
      })
      .addCase(createRestaurantMenuEquipment.rejected, (state, action) => {
        state.restaurantMenuEquipments.loadingState = 'error'
        state.restaurantMenuEquipments.error = action.error
      })

      // Update restaurant menu equipment
      .addCase(updateRestaurantMenuEquipment.pending, state => {
        state.restaurantMenuEquipments.loadingState = 'pending'
      })
      .addCase(updateRestaurantMenuEquipment.fulfilled, (state, action) => {
        state.restaurantMenuEquipments.loadingState = 'success'

        // Update the item in the list
        const index = state.restaurantMenuEquipments.value.findIndex(item => item.id === action.payload.id)
        if (index !== -1) {
          state.restaurantMenuEquipments.value[index] = action.payload
        }
      })
      .addCase(updateRestaurantMenuEquipment.rejected, (state, action) => {
        state.restaurantMenuEquipments.loadingState = 'error'
        state.restaurantMenuEquipments.error = action.error
      })

      // Bulk update restaurant menu equipment status
      .addCase(bulkUpdateRestaurantMenuEquipmentStatus.pending, state => {
        state.restaurantMenuEquipments.loadingState = 'pending'
      })
      .addCase(bulkUpdateRestaurantMenuEquipmentStatus.fulfilled, (state, action) => {
        state.restaurantMenuEquipments.loadingState = 'success'

        // Update the items in the list with the updated statuses
        action.payload.updated.forEach((updatedItem: BulkUpdateStatusResponse['updated'][0]) => {
          const index = state.restaurantMenuEquipments.value.findIndex(item => item.id === updatedItem.id)
          if (index !== -1) {
            state.restaurantMenuEquipments.value[index] = {
              ...state.restaurantMenuEquipments.value[index],
              isActive: updatedItem.isActive
            }
          }
        })
      })
      .addCase(bulkUpdateRestaurantMenuEquipmentStatus.rejected, (state, action) => {
        state.restaurantMenuEquipments.loadingState = 'error'
        state.restaurantMenuEquipments.error = action.error
      })
  }
})

// Fetch equipment mappings by equipment ID
export const fetchEquipmentMappings = createAsyncThunk<MenuEquipmentProductMapping[], number, Redux>(
  FETCH_EQUIPMENT_MAPPINGS,
  async (menuEquipmentId: number, { extra }) => {
    try {
      const response = await extra.httpClient.get<
        MenuEquipmentProductMapping[] | { data: MenuEquipmentProductMapping[] }
      >(`${MENU_EQUIPMENT_PRODUCTS_BASE_URL}/equipment/${menuEquipmentId}`)

      // Handle different response structures
      if (Array.isArray(response.data)) {
        return response.data
      } else if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data
      }

      return []
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to fetch equipment mappings')
      }
      throw new Error('Failed to fetch equipment mappings')
    }
  }
)

// Fetch single equipment mapping by ID
export const fetchEquipmentMappingById = createAsyncThunk<MenuEquipmentProductMapping, number, Redux>(
  FETCH_EQUIPMENT_MAPPING_BY_ID,
  async (id: number, { extra }) => {
    try {
      const response = await extra.httpClient.get<MenuEquipmentProductMapping | { data: MenuEquipmentProductMapping }>(
        `${MENU_EQUIPMENT_PRODUCTS_BASE_URL}/${id}`
      )

      // Handle different response structures
      if ('data' in response.data) {
        return response.data.data
      }

      return response.data as MenuEquipmentProductMapping
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to fetch equipment mapping')
      }
      throw new Error('Failed to fetch equipment mapping')
    }
  }
)

// Create equipment mapping
export const createEquipmentMapping = createAsyncThunk<
  MenuEquipmentProductMapping,
  CreateMenuEquipmentProductMappingDto,
  Redux
>(CREATE_EQUIPMENT_MAPPING, async (data: CreateMenuEquipmentProductMappingDto, { extra }) => {
  try {
    const response = await extra.httpClient.post<{ data: MenuEquipmentProductMapping }>(
      MENU_EQUIPMENT_PRODUCTS_BASE_URL,
      data
    )

    return response.data.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to create equipment mapping')
    }
    throw new Error('Failed to create equipment mapping')
  }
})

// Update equipment mapping
export const updateEquipmentMapping = createAsyncThunk<
  MenuEquipmentProductMapping,
  { id: number; data: UpdateMenuEquipmentProductMappingDto },
  Redux
>(UPDATE_EQUIPMENT_MAPPING, async ({ id, data }, { extra }) => {
  try {
    const response = await extra.httpClient.patch<{ data: MenuEquipmentProductMapping }>(
      `${MENU_EQUIPMENT_PRODUCTS_BASE_URL}/${id}`,
      data
    )

    return response.data.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to update equipment mapping')
    }
    throw new Error('Failed to update equipment mapping')
  }
})

// Delete equipment mapping
export const deleteEquipmentMapping = createAsyncThunk<number, number, Redux>(
  DELETE_EQUIPMENT_MAPPING,
  async (id: number, { extra }) => {
    try {
      await extra.httpClient.delete(`${MENU_EQUIPMENT_PRODUCTS_BASE_URL}/${id}`)

      return id
    } catch (e) {
      if (e instanceof AxiosError) {
        throw new Error(e.response?.data?.message || 'Failed to delete equipment mapping')
      }
      throw new Error('Failed to delete equipment mapping')
    }
  }
)

// Bulk upsert equipment mappings (replaces all mappings for equipment)
export const bulkUpsertEquipmentMappings = createAsyncThunk<
  MenuEquipmentProductMapping[],
  BulkUpsertMenuEquipmentProductMappingDto[],
  Redux
>(BULK_UPSERT_EQUIPMENT_MAPPINGS, async (mappings: BulkUpsertMenuEquipmentProductMappingDto[], { extra }) => {
  try {
    const response = await extra.httpClient.post<BulkUpsertMenuEquipmentProductMappingResponse>(
      `${MENU_EQUIPMENT_PRODUCTS_BASE_URL}/bulk-upsert`,
      mappings
    )

    return response.data.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to bulk upsert equipment mappings')
    }
    throw new Error('Failed to bulk upsert equipment mappings')
  }
})

// Bulk upsert restaurant menu equipments
export const bulkUpsertRestaurantMenuEquipments = createAsyncThunk<
  RestaurantMenuEquipment[],
  CreateRestaurantMenuEquipmentDto[],
  Redux
>(BULK_UPSERT_RESTAURANT_MENU_EQUIPMENTS, async (links: CreateRestaurantMenuEquipmentDto[], { extra }) => {
  try {
    const response = await extra.httpClient.post<BulkUpsertRestaurantMenuEquipmentResponse>(
      `${RESTAURANT_MENU_EQUIPMENT_BASE_URL}/bulk-upsert`,
      links
    )

    return response.data.data || []
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to link equipment to restaurants')
    }
    throw new Error('Failed to link equipment to restaurants')
  }
})

// Fetch restaurant menu equipments
export const fetchRestaurantMenuEquipments = createAsyncThunk<
  RestaurantMenuEquipment[],
  { menuEquipmentId?: number; restaurantId?: number },
  Redux
>(FETCH_RESTAURANT_MENU_EQUIPMENTS, async ({ menuEquipmentId, restaurantId }, { extra }) => {
  try {
    const params: any = {}
    if (menuEquipmentId) params.menuEquipmentId = menuEquipmentId
    if (restaurantId) params.restaurantId = restaurantId

    const response = await extra.httpClient.get<RestaurantMenuEquipment[] | { data: RestaurantMenuEquipment[] }>(
      RESTAURANT_MENU_EQUIPMENT_BASE_URL,
      {
        params
      }
    )

    // Handle different response structures
    if (Array.isArray(response.data)) {
      return response.data
    } else if (response.data.data && Array.isArray(response.data.data)) {
      return response.data.data
    }

    return []
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to fetch restaurant menu equipments')
    }
    throw new Error('Failed to fetch restaurant menu equipments')
  }
})

// Create restaurant menu equipment link
export const createRestaurantMenuEquipment = createAsyncThunk<
  RestaurantMenuEquipment,
  CreateRestaurantMenuEquipmentDto,
  Redux
>(CREATE_RESTAURANT_MENU_EQUIPMENT, async (data: CreateRestaurantMenuEquipmentDto, { extra }) => {
  try {
    const response = await extra.httpClient.post<{ data: RestaurantMenuEquipment }>(
      RESTAURANT_MENU_EQUIPMENT_BASE_URL,
      data
    )

    return response.data.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to create restaurant menu equipment link')
    }
    throw new Error('Failed to create restaurant menu equipment link')
  }
})

// Update restaurant menu equipment link
export const updateRestaurantMenuEquipment = createAsyncThunk<
  RestaurantMenuEquipment,
  { id: number; data: UpdateRestaurantMenuEquipmentDto },
  Redux
>(UPDATE_RESTAURANT_MENU_EQUIPMENT, async ({ id, data }, { extra }) => {
  try {
    const response = await extra.httpClient.patch<{ data: RestaurantMenuEquipment }>(
      `${RESTAURANT_MENU_EQUIPMENT_BASE_URL}/${id}`,
      data
    )

    return response.data.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to update restaurant menu equipment link')
    }
    throw new Error('Failed to update restaurant menu equipment link')
  }
})

// Bulk update restaurant menu equipment status
export const previewBulkUpdateRestaurantMenuEquipmentStatus = createAsyncThunk<
  PreviewResponse,
  BulkUpdateStatusRequestDto,
  Redux
>(PREVIEW_BULK_UPDATE_RESTAURANT_MENU_EQUIPMENT_STATUS, async (data: BulkUpdateStatusRequestDto, { extra }) => {
  try {
    const response = await extra.httpClient.post<PreviewResponse>(
      `${RESTAURANT_MENU_EQUIPMENT_BASE_URL}/preview-bulk-update-status-with-menu-enablement`,
      data
    )

    return response.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to preview bulk update restaurant menu equipment status')
    }
    throw new Error('Failed to preview bulk update restaurant menu equipment status')
  }
})

export const bulkUpdateRestaurantMenuEquipmentStatus = createAsyncThunk<
  BulkUpdateStatusResponse,
  BulkUpdateStatusRequestDto,
  Redux
>(BULK_UPDATE_RESTAURANT_MENU_EQUIPMENT_STATUS, async (data: BulkUpdateStatusRequestDto, { extra }) => {
  try {
    const response = await extra.httpClient.patch<BulkUpdateStatusResponse>(
      `${RESTAURANT_MENU_EQUIPMENT_BASE_URL}/bulk-update-status-with-menu-enablement`,
      data
    )

    return response.data
  } catch (e) {
    if (e instanceof AxiosError) {
      throw new Error(e.response?.data?.message || 'Failed to bulk update restaurant menu equipment status')
    }
    throw new Error('Failed to bulk update restaurant menu equipment status')
  }
})

export const {
  clearCreatedMenuEquipment,
  clearUpdatedMenuEquipment,
  clearMenuEquipment,
  invalidateMenuEquipments,
  clearMenuEquipments,
  clearEquipmentMappings,
  clearEquipmentMapping,
  clearCreatedEquipmentMapping,
  clearUpdatedEquipmentMapping,
  clearBulkUpsertEquipmentMappings,
  clearRestaurantMenuEquipments,
  clearBulkUpsertRestaurantMenuEquipments
} = menuEquipmentsSlice.actions

export default menuEquipmentsSlice.reducer
